import axios from "axios";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

export const extractError = (e, fallback) => {
    const detail = e.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(d => d.msg).join(", ");
    return e.message || fallback;
};

/* ── {{job_role}} ────────────────────────────────────────────────────────────
   An outreach is written once and reused across job roles, so the role can't be
   baked into the copy — it has to be a token, like the candidate_* ones.

   Unlike those, this one is resolved here rather than server-side: the role isn't a
   property of the candidate, it's a property of *this enrollment*, and the job in
   scope is already threaded to every enroll entry point. The send-time renderer
   doesn't know the token, which is what lets it survive the preview round-trip
   intact and reach us here. */

export const JOB_ROLE_TOKEN = "{{job_role}}";
const JOB_ROLE_PATTERN = "\\{\\{\\s*job_role\\s*\\}\\}";

const escapeHtml = (s) =>
    s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export const hasJobRoleToken = (text) => new RegExp(JOB_ROLE_PATTERN, "i").test(text || "");

/**
 * Swap the token for the role this enrollment is for. `html` escapes the role, since
 * bodies are Quill markup and a role like "R&D Lead" would otherwise break it.
 *
 * Falls back to "this role" rather than leaving the token in place: an unresolved
 * "{{job_role}}" reaching a candidate's inbox is far worse than a vague sentence.
 */
export const resolveJobRole = (text, jobRole, { html = false } = {}) => {
    if (!text) return text;
    const label = (jobRole || "").trim() || "this role";
    return text.replace(new RegExp(JOB_ROLE_PATTERN, "gi"), html ? escapeHtml(label) : label);
};

/** Resolve the token across a set of preview steps ({ step_id, email: { subject, body } }). */
export const resolveStepsJobRole = (steps, jobRole) =>
    (steps || []).map((s) =>
        s.email
            ? {
                ...s,
                email: {
                    ...s.email,
                    subject: resolveJobRole(s.email.subject, jobRole),
                    body: resolveJobRole(s.email.body, jobRole, { html: true }),
                },
            }
            : s
    );

const emptyOverrides = (candidateIds) => Object.fromEntries(candidateIds.map((id) => [id, []]));

/**
 * The direct "Enroll" path never renders the copy, so a {{job_role}} sitting in the
 * outreach's templates would ship as literal text. Render it the same way the preview
 * screen does, and only take over the copy if the token is actually in use — outreach
 * without it keeps sending empty overrides, so the send-time renderer stays in charge.
 */
const directOverrides = async ({ candidateIds, sequenceId, jobRole }) => {
    let candidates;
    try {
        const { data } = await axios.post(`${SOURCING_URL}/email/enrollments/preview`, {
            candidate_ids: candidateIds,
            sequence_id: sequenceId,
            org_id: localStorage.getItem("current_org_id"),
            created_by: localStorage.getItem("org_admin_id"),
        });
        candidates = data.candidates || [];
    } catch {
        // The preview is only here to resolve a token. If it fails, enrolling on the
        // stored templates is still the right outcome for the vast majority of outreach.
        return emptyOverrides(candidateIds);
    }

    const usesToken = candidates.some((pc) =>
        (pc.steps || []).some((s) => hasJobRoleToken(s.email?.subject) || hasJobRoleToken(s.email?.body))
    );
    if (!usesToken) return emptyOverrides(candidateIds);

    const byCandidate = Object.fromEntries(
        candidates.map((pc) => [
            pc.candidate_id,
            resolveStepsJobRole(pc.steps, jobRole)
                .filter((s) => s.email)
                .map((s) => ({ step_id: s.step_id, subject: s.email.subject, body: s.email.body })),
        ])
    );
    // Anyone the preview didn't cover still needs a key, or they'd enroll without one.
    return { ...emptyOverrides(candidateIds), ...byCandidate };
};

/**
 * Enroll candidates in an outreach and fold the per-candidate results into the one
 * summary shape every caller drops into its toast.
 *
 * Shared by the two ways into enrollment: straight from the outreach picker
 * (AddToSequenceModal's "Enroll") and after editing copy (PreviewModal's "Add to
 * Outreach"), so both report success and failure identically.
 *
 *   candidateIds – ids to enroll
 *   sequenceId   – chosen outreach
 *   jobId        – job role this journey is scoped to; stamped on the enrollment
 *   overrides    – optional { candidate_id: [{ step_id, subject, body }] }. Omitted
 *                  means "send the outreach's own templates", which is the direct path.
 */
export const enrollCandidates = async ({ candidateIds, sequenceId, jobId, jobRole, overrides }) => {
    const resolved = overrides
        ? // The preview path hands us the copy the recruiter just saw. Re-resolving is a
          // no-op unless they typed the token back in while editing.
          Object.fromEntries(
              Object.entries(overrides).map(([id, steps]) => [
                  id,
                  (steps || []).map((s) => ({
                      ...s,
                      subject: resolveJobRole(s.subject, jobRole),
                      body: resolveJobRole(s.body, jobRole, { html: true }),
                  })),
              ])
          )
        : await directOverrides({ candidateIds, sequenceId, jobRole });

    const { data } = await axios.post(`${SOURCING_URL}/email/enrollments`, {
        candidate_ids: candidateIds,
        sequence_id: sequenceId,
        org_id: localStorage.getItem("current_org_id"),
        created_by: localStorage.getItem("org_admin_id"),
        job_id: jobId || null,
        overrides: resolved,
    });

    const results = data.results || [];
    const enrolled = results.filter(r => r.status === "enrolled").length;
    const skipped = results.filter(r => r.status === "skipped").length;
    const failed = results.filter(r => r.status === "error").length;
    // Distinct reasons across skipped + failed, so the recruiter sees *why* a candidate
    // wasn't added rather than a blanket success.
    const reasons = [...new Set(
        results.filter(r => r.status !== "enrolled").map(r => r.reason).filter(Boolean)
    )];
    const parts = [];
    if (enrolled) parts.push(`${enrolled} added to outreach`);
    if (skipped) parts.push(`${skipped} skipped`);
    if (failed) parts.push(`${failed} failed`);
    let message = parts.join(" · ") || "No candidates were processed";
    if (reasons.length) message += ` — ${reasons.join("; ")}`;
    const tone = failed > 0 ? "error" : skipped > 0 ? "warn" : "success";

    return { enrolled, skipped, failed, tone, message };
};
